import { AxiosResponse } from "axios";
import { Action } from "redux";
import { ColorSchemesAPI } from "../../server/types/index.js";
import {
  ColorScheme,
  ColorSchemeCategory,
  ColorSchemePutData,
  ThunkAction,
} from "../types/index.js";
import { ColorSchemeNotFoundError, fetch } from "../utils/utils.js";
import { showAlert } from "./Alerts.js";
import { deleteModulesColorScheme } from "./Modules.js";
import { isActionType } from "./utils.js";

const ActionTypeKeys = [
  "CHANGE_COLOR",
  "DUPLICATE_COLOR_SCHEME_START",
  "GET_COLOR_SCHEMES_SUCCESS",
  "GET_COLOR_SCHEMES_START",
  "GET_COLOR_SCHEMES_ERROR",
  "PUT_COLOR_SCHEME_START",
  "DELETE_COLOR_SCHEME_START",
  "DELETE_COLOR_SCHEME_ERROR",
  "UPDATE_COLOR_SCHEME",
] as const;

type ActionType = (typeof ActionTypeKeys)[number];
type ActionBase<T extends ActionType> = Action<T>;

type ChangeColor = ActionBase<"CHANGE_COLOR"> & {
  schemeId: string;
  categoryId: ColorSchemeCategory;
  colorType: string;
  color: string;
};
type DuplicateColorSchemeStart = ActionBase<"DUPLICATE_COLOR_SCHEME_START"> & {
  schemeId: string;
  newId: string;
};
type GetColorSchemesSuccess = ActionBase<"GET_COLOR_SCHEMES_SUCCESS"> & {
  serverSchemes: ColorSchemesAPI;
};
type GetColorSchemesStart = ActionBase<"GET_COLOR_SCHEMES_START">;
type GetColorSchemesError = ActionBase<"GET_COLOR_SCHEMES_ERROR">;
type PutColorSchemeStart = ActionBase<"PUT_COLOR_SCHEME_START"> & {
  colorSchemeId: string;
  scheme: ColorScheme;
};
type DeleteColorSchemeStart = ActionBase<"DELETE_COLOR_SCHEME_START"> & {
  colorSchemeId: string;
};
type DeleteColorSchemeError = ActionBase<"DELETE_COLOR_SCHEME_ERROR"> & {
  prevState: ColorSchemesAPI;
};
type UpdateColorScheme = ActionBase<"UPDATE_COLOR_SCHEME"> & {
  colorSchemeId: string;
  scheme: ColorScheme;
};

export type ColorSchemesAction =
  | ChangeColor
  | DuplicateColorSchemeStart
  | GetColorSchemesSuccess
  | GetColorSchemesStart
  | GetColorSchemesError
  | PutColorSchemeStart
  | DeleteColorSchemeStart
  | DeleteColorSchemeError
  | UpdateColorScheme;

export const isColorSchemesAction = (
  action: Action,
): action is ColorSchemesAction => isActionType(action, ActionTypeKeys);

export const changeColor = (
  schemeId: string,
  categoryId: ColorSchemeCategory,
  colorType: string,
  color: string,
): ChangeColor => ({
  type: "CHANGE_COLOR",
  schemeId,
  categoryId,
  colorType,
  color,
});

export const getColorSchemes = (siteId: string): ThunkAction<Promise<void>> => {
  return (dispatch, getState) => {
    if (getState().loadStates.colorSchemes !== "unloaded") {
      return Promise.resolve();
    }

    dispatch(getColorSchemesStart());

    return fetch({ dispatch })
      .get(`sites/${siteId}/color-schemes`)
      .then((res) => {
        dispatch(getColorSchemesSuccess(res.data));
      })
      .catch((error) => {
        dispatch(showAlert("Die Farbschemen konnten nicht geladen werden!"));
        dispatch(getColorSchemesError());
        throw error;
      });
  };
};

export const getColorSchemesSuccess = (
  serverSchemes: ColorSchemesAPI,
): GetColorSchemesSuccess => ({
  type: "GET_COLOR_SCHEMES_SUCCESS",
  serverSchemes,
});

const getColorSchemesStart = (): GetColorSchemesStart => ({
  type: "GET_COLOR_SCHEMES_START",
});

const getColorSchemesError = (): GetColorSchemesError => ({
  type: "GET_COLOR_SCHEMES_ERROR",
});

export const putColorScheme = (
  siteId: string,
  colorSchemeId: string,
): ThunkAction<Promise<AxiosResponse>> => {
  return (dispatch, getState) => {
    const scheme = getState().colorSchemes.schemes[colorSchemeId];
    if (!scheme) throw new ColorSchemeNotFoundError(colorSchemeId);
    dispatch(putColorSchemeStart(colorSchemeId, scheme));

    const colorSchePutData: ColorSchemePutData = { scheme };

    return fetch({ dispatch })
      .put(`sites/${siteId}/color-schemes/${colorSchemeId}`, colorSchePutData)
      .catch((error) => {
        dispatch(
          showAlert("Das Aktualisieren des Farbschemen ist fehlgeschlagen!"),
        );
        throw error;
      });
  };
};

export const updateColorScheme = (
  colorSchemeId: string,
  scheme: ColorScheme,
): UpdateColorScheme => ({
  type: "UPDATE_COLOR_SCHEME",
  colorSchemeId,
  scheme,
});

export const putColorSchemeStart = (
  colorSchemeId: string,
  scheme: ColorScheme,
): PutColorSchemeStart => ({
  type: "PUT_COLOR_SCHEME_START",
  colorSchemeId,
  scheme,
});

export const deleteColorScheme = (
  siteId: string,
  colorSchemeId: string,
): ThunkAction<Promise<AxiosResponse>> => {
  return (dispatch, getState) => {
    const prevState = getState().colorSchemes;

    dispatch(deleteColorSchemeStart(colorSchemeId));
    dispatch(deleteModulesColorScheme(colorSchemeId));

    return fetch({ dispatch })
      .delete(`sites/${siteId}/color-schemes/${colorSchemeId}`)
      .catch((error) => {
        dispatch(deleteColorSchemeError(prevState));
        dispatch(
          showAlert("Beim Löschen des Farbschemen ist ein Fehler aufgetreten!"),
        );
        throw error;
      });
  };
};

export const deleteColorSchemeStart = (
  colorSchemeId: string,
): DeleteColorSchemeStart => ({
  type: "DELETE_COLOR_SCHEME_START",
  colorSchemeId,
});

const deleteColorSchemeError = (
  prevState: ColorSchemesAPI,
): DeleteColorSchemeError => ({ type: "DELETE_COLOR_SCHEME_ERROR", prevState });

export const duplicateColorScheme = (
  siteId: string,
  colorSchemeId: string,
): ThunkAction<void> => {
  const newId = crypto.randomUUID();

  return (dispatch) => {
    dispatch(duplicateColorSchemeStart(colorSchemeId, newId));
    dispatch(putColorScheme(siteId, newId));
  };
};

const duplicateColorSchemeStart = (
  schemeId: string,
  newId: string,
): DuplicateColorSchemeStart => ({
  type: "DUPLICATE_COLOR_SCHEME_START",
  schemeId,
  newId,
});
